/**
 * Component for submitting Form Assembly forms.
 */
class FormAssemblySubmit {
  /**
   * Start the component.
   */
  constructor(form) {
    if (null == form || ! (form instanceof HTMLFormElement)) {
      throw new Error('Invalid form element.');
    }

    /**
     * The form to act upon.
     *
     * @type {HTMLFormElement}
     */
    this.form = form;

    // Bind class methods.
    this.validateEmailInput = this.validateEmailInput.bind(this);
    this.formSubmit = this.formSubmit.bind(this);
    this.checkEmailInput = this.checkEmailInput.bind(this);
    this.updateState = this.updateState.bind(this);
    this.updateReadyState = this.updateReadyState.bind(this);

    this.init();
  }

  /**
   * Dispatch the `sn.formstatechange` event.
   *
   * @param {String} status The form status.
   */
  updateState(status) {
    const event = new CustomEvent(
      'sn.formstatechange',
      {
        bubbles: true,
        composed: true,
        detail: {
          status,
        },
      },
    );

    this.form.dispatchEvent(event);
  }

  /**
   * Update the form ready state and dispatch the `sn.formready` event.
   */
  updateReadyState() {
    Object.defineProperty(
      this.form,
      'faReadyState',
      { value: 'ready', configurable: true },
    );

    const event = new CustomEvent(
      'sn.formready',
      {
        bubbles: true,
        composed: true,
        detail: {},
      },
    );

    this.form.dispatchEvent(event);
  }

  /**
   * Initialize the Form Assembly helper.
   */
  init() {
    this.emailInput = this.form.querySelector('[name="email"]');
    this.nonceInput = this.form.querySelector('[name="nonce"]');
    this.submit = this.form.querySelector('[type="submit"]');
    this.honeyPot = this.form.querySelector('[name="client_key"]');
    this.formId = this.form.querySelector('[name="form_id"]');
    this.control = this.form.querySelector('[name="control"]');

    if (null != this.emailInput) {
      this.form.addEventListener('submit', this.formSubmit);
      this.emailInput.addEventListener('input', this.checkEmailInput);

      // Signal that the form is ready.
      this.updateReadyState();
    }
  }

  /**
   * Remove the invalid state when the input value is updated.
   */
  checkEmailInput() {
    if (null !== this.emailInput.getAttribute('aria-invalid')) {
      this.emailInput.removeAttribute('aria-invalid');
    }
  }

  /**
   * Ensure the email value is valid according to the input's `pattern` attribute.
   *
   * @returns {Boolean}
   */
  validateEmailInput() {
    const { value, pattern } = this.emailInput;

    const patternRegExp = new RegExp(pattern);
    const fieldValue = value.trim();

    return (0 < value.trim().length && patternRegExp.test(fieldValue));
  }

  /**
   * Form submission.
   *
   * @param {Event} event The event object.
   */
  formSubmit(event) { // eslint-disable-line consistent-return
    event.preventDefault();

    if (! this.validateEmailInput()) {
      this.emailInput.setAttribute('aria-invalid', 'true');
      return false;
    }

    const { pathname, search } = window.location;

    // Get hidden input values.
    const honeyPotValue = (this.honeyPot?.value) ? this.honeyPot.value : '';
    const nonceValue = this.nonceInput.value;
    const formId = this.formId.value;
    const control = this.control.value;

    // Email is the only forward-facing input.
    const emailValue = encodeURIComponent(this.emailInput.value);

    // Disable and clear error state.
    this.emailInput.removeAttribute('aria-invalid');
    this.updateState('loading');

    // Build request string.
    const parts = [
      `client_key=${honeyPotValue}`,
      `control=${control}`,
      `email=${emailValue}`,
      `form_id=${formId}`,
      `page=${pathname}`,
    ];
    const string = `${parts.join('&')}${search}`;

    const request = new XMLHttpRequest();

    // Make AJAX request.
    request.open('POST', this.form.action, true);
    request.setRequestHeader(
      'Content-Type',
      'application/x-www-form-urlencoded; charset=UTF-8',
    );
    request.setRequestHeader('X-WP-Nonce', nonceValue);
    request.send(encodeURI(string));

    // On completion.
    request.onreadystatechange = () => {
      if (request.readyState === XMLHttpRequest.DONE) {
        const { status } = request;

        if (0 === status || (200 <= status && 400 > status)) {
          this.updateState('success');
        } else {
          this.updateState('error');
        }
      }
    };
  }
}

/**
 * Update classes to reflect the form state.
 *
 * @param {HTMLElement} element The element upon which state classes should be applied
 * @param {Object}      details The event details.
 */
function setFormAssemblyState(element, { status }) {
  const classes = {
    loading: 'login-form--loading',
    success: 'login-form--success',
    error: 'login-form--error',
  };

  requestAnimationFrame(() => {
    // Clear state classes.
    if ('loading' === status) {
      element.classList.remove(classes.success, classes.error);
    } else {
      element.classList.remove(classes.loading);
    }

    // Add the status class.
    element.classList.add(classes[status]);
  });
}

export {
  FormAssemblySubmit,
  setFormAssemblyState,
};
